import styled from '@emotion/styled';
import { Button, Input, notification, Select, Switch, Tag, Upload } from 'antd';
import { useEffect, useState } from 'react';
import { FaCircle, FaSave, FaUpload } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { DetailBondEntity } from '../../../../entities/detailBond.entity';
import { DetenteurEntity } from '../../../../entities/detenteur.entity';
import { ORANGE } from '../../../../shared/colors';
import { formatNum } from '../../../../shared/constants';
import { getDetenteurs, operationBond } from '../network';
import { getSchema } from './getSchema';

const Container = styled.div`
  > .top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }
`;

export const OperationTitreForm = ({
  setShowModal,
  detailBond,
}: {
  setShowModal: (b: boolean) => void;
  detailBond?: DetailBondEntity;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [full, setFull] = useState(true); // effectuer une opération sur la totalité ou une partie du titre
  const [operation, setOperation] = useState('');
  const [qte, setQte] = useState(detailBond?.qte || 0);
  const [file, setFile] = useState<any>();
  const [receveur, setReceveur] = useState('');
  const [detenteurs, setDetenteurs] = useState<DetenteurEntity[]>([]);
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;

  useEffect(() => {
    getDetenteurs(connectedUser.id).then(({ data }) => {
      setDetenteurs(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const needsReceveur = () => {
    if (
      ['mise_pension_titre', 'pret_titre', 'ventre_titre'].includes(operation)
    )
      return true;
    else return false;
  };

  const submitOperation = async () => {
    setIsLoading(true);

    if (detailBond) {
      const dataToCreate = {
        quantite: qte,
        full,
        operationType: operation,
        // schéma comptable
        schema: getSchema(
          connectedUser,
          detailBond.detenteur,
          operation as any,
        ),
        // ID du bond
        detailBondId: detailBond.id,
        userId: connectedUser.id,
        receveurId: receveur,
        // elements de controle
        eltControl:
          operationValues.find((val) => val.value === operation)?.label || '',
        media: file?.fileList[0]?.originFileObj,
        // info du compte
        code_svt: connectedUser.partenaire.code_referent,
        code_client: detailBond.detenteur.code_referent,
        code_agence: connectedUser.agence.code,
      };

      console.log(dataToCreate);
      setIsLoading(false);

      await operationBond(dataToCreate).then(({ data, statusCode }) => {
        if (statusCode?.toString()[0] === '2') {
          notification.success({
            message: 'Succès',
            description: "L'opération sur le titre a été effectue avec succès",
          });
          setShowModal(false);
        } else {
          notification.info({
            message: 'Erreur ' + statusCode.toString(),
            description: data.message,
          });
        }
        setIsLoading(false);
      });
    }
  };

  return (
    <Container>
      <div className='top'>
        <div>
          <b>Code Titre :</b>{' '}
          <Tag color='processing'>{detailBond?.parentCode} </Tag>{' '}
        </div>
        <div>
          <b>Sous Code :</b>{' '}
          <Tag color='processing'>{detailBond?.subCode} </Tag>
        </div>
      </div>

      <div className='top'>
        <div>
          <b>Quantité total :</b> {formatNum(detailBond?.qte)}
        </div>
        <FaCircle size={5} />
        <div>
          <b>Code client :</b> {detailBond?.detenteur.code_referent}
        </div>
        <FaCircle size={5} />
        <div>
          <b>Etat :</b> {detailBond?.etat}{' '}
        </div>
      </div>

      <Select
        placeholder="Sélectionnez l'opération à effectuer"
        style={{ display: 'block', width: '100%', marginBottom: 15 }}
        onChange={(val) => setOperation(val)}
      >
        {operationValues.map((op) => (
          <Select.Option key={op.value} value={op.value}>
            {op.label}
          </Select.Option>
        ))}
      </Select>

      <div style={{ marginBottom: 15 }}>
        Effectuer l'opération sur la totalité du titre :{'  '}
        <Switch
          defaultChecked
          onChange={(checked: boolean) => setFull(checked)}
        />
      </div>

      {!full && (
        <Input
          style={{ marginBottom: 15 }}
          placeholder='Entrez la quantité'
          type='number'
          min={0}
          onChange={(e) => setQte(Number(e.target.value))}
        />
      )}

      <Upload
        name='piece'
        onChange={(fichier) => {
          setFile(fichier as any);
        }}
      >
        <span style={{ color: 'black' }}>
          Pièce justificative <i>(facultatif)</i> :{' '}
        </span>
        <Button icon={<FaUpload style={{ marginRight: 10 }} />}>
          Sélectionner le fichier
        </Button>
      </Upload>

      {needsReceveur() && (
        <div>
          <p style={{ marginTop: 15, marginBottom: 5 }}>Receveur</p>
          <Select
            placeholder='Sélectionnez le receveur'
            style={{ display: 'block', width: '100%' }}
            onChange={(val) => setReceveur(val)}
          >
            {detenteurs
              .filter(
                (det) =>
                  det.code_referent !== detailBond?.detenteur.code_referent,
              )
              .map((det) => (
                <Select.Option key={det.id} value={det.id}>
                  {det.code_referent} - {det.nom} {det.prenom}
                </Select.Option>
              ))}
          </Select>
        </div>
      )}

      <div
        style={{
          marginTop: 20,
          display: 'flex',
          justifyContent: 'right',
        }}
      >
        <Button
          type='primary'
          loading={isLoading}
          icon={<FaSave style={{ marginBottom: -1.8, marginRight: 4 }} />}
          style={{
            backgroundColor: ORANGE,
            borderWidth: 0,
            borderRadius: 5,
          }}
          onClick={submitOperation}
        >
          Enregistrer
        </Button>
      </div>
    </Container>
  );
};

const operationValues = [
  {
    label: 'Livraison de titre',
    value: 'liv_titre',
  },
  {
    label: 'Vente de titre',
    value: 'ventre_titre',
  },
  {
    label: 'Mise en pension',
    value: 'mise_pension_titre',
  },
  {
    label: 'Restitution titre en pension livrée',
    value: 'restitu_pension_titre',
  },
  {
    label: 'Défaut de restitution titre en pension livrée',
    value: 'defaut_pension_titre',
  },
  {
    label: 'Prêt de titre',
    value: 'pret_titre',
  },
  {
    label: 'Restitution prêt de titre',
    value: 'restitu_pret_titre',
  },
  {
    label: 'Défaut prêt de titre',
    value: 'defaut_pret_titre',
  },
  {
    label: 'Transfert de titres reçus à la CRCT',
    value: 'transf_titre_CRCT',
  },
  {
    label: 'Transfert par la CRCT vers autre SVT',
    value: 'transf_titre_SVT',
  },
  {
    label: 'Non remboursement de titre échus',
    value: 'non_remb_titre',
  },
  {
    label: 'Remboursement de titre échus',
    value: 'remb_titre',
  },
  {
    label: 'Litige',
    value: 'litige_titre',
  },
  {
    label: 'Fin du litige',
    value: 'fin_litige_titre',
  },
];
